import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { firstValueFrom, forkJoin, map } from 'rxjs';
import { INSTRUCTIONS } from 'src/app/pages/ai/services/instructions.constants';
import {
  GeminiSchemaType,
  ProxyAiResponseSchemaService,
} from 'src/app/pages/ai/services/proxy-ai-response-schema.service';
import { TechStackInstallDto } from 'src/portal/admin-buyerscaddy-api/models/tech-stack-install-dto';
import { InstallService } from 'src/portal/admin-buyerscaddy-api/services/install.service';
import { ProductDto } from 'src/portal/public-api/models/product-dto';
import { VendorService } from 'src/portal/public-api/services/vendor.service';
import { CompanyDto } from 'src/portal/search-api/models/company-dto';
import { CompanyPageRequestDto } from 'src/portal/search-api/models/company-page-request-dto';
import { GlobalSearchResultTypeEnum } from 'src/portal/search-api/models/global-search-result-type-enum';
import { CompanyService } from 'src/portal/search-api/services/company.service';
import { ProductSelectDialogComponent } from '../product-select-dialog/product-select-dialog.component';

export interface ImportCompanyProductsDialogData {
  data?: string;
  title?: string;
}

interface ImportRow {
  ProductName?: string;
  ProductVendorDomainName?: string;
  CompanyDomainName?: string;
  CompanyName?: string;
  Country?: string;
  Industry?: string;
  Probability?: string;
  Source?: string;
  dateFirstVerified?: string;
  dateLastVerified?: string;
  selectedCompany?: ProductDto[] | null;
  isSelected?: boolean;
  foundCount?: number;
  product?: ProductDto | null;
}

@Component({
  selector: 'import-company-products',
  templateUrl: './import-company-products.component.html',
  styleUrls: ['./import-company-products.component.scss'],
})
export class ImportCompanyProductsComponent implements OnInit {
  companyLabel: string | null = null;

  readonly displayedColumns: string[] = [
    'selected',
    'ProductName',
    'Country',
    'Industry',
    'Probability',
    'Source',
    'dateFirstVerified',
    'dateLastVerified',
  ];

  loading: boolean = false;
  rows: ImportRow[] = [];
  selectedProducts: ProductDto[] = [];
  selectedCompany: CompanyDto | null = null;
  importing = false;
  companyNotFound = false;

  get canImport(): boolean {
    const companyId = this.selectedCompany?.id;
    const hasProducts = this.selectedProducts.some((product) => !!product?.id);

    return !!companyId && hasProducts && !this.loading && !this.importing;
  }

  get selectedProductCount(): number {
    return this.rows.filter((row) => row.isSelected).length;
  }

  get skippedProductCount(): number {
    if (!this.rows?.length) {
      return 0;
    }

    return this.rows.length - this.selectedProductCount;
  }

  get companyPossessiveName(): string {
    const companyName = this.selectedCompany?.companyName?.trim();
    if (companyName) {
      return `${companyName}'s`;
    }

    return "this company's";
  }
  constructor(
    private readonly dialogRef: MatDialogRef<ImportCompanyProductsComponent>,
    @Inject(MAT_DIALOG_DATA)
    private readonly dialogData: ImportCompanyProductsDialogData,
    private readonly dialog: MatDialog,
    private readonly companyService: CompanyService,
    private vendorService: VendorService,
    private readonly installService: InstallService,
    private proxyAiResponseSchemaService: ProxyAiResponseSchemaService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.companyLabel = '';
    this.getData(this.dialogData.data);
  }

  async getData(source): Promise<void> {
    this.loading = true;
    this.selectedProducts = [];
    this.companyNotFound = false;
    console.log(source);

    const promptPayload =
      typeof source === 'string' ? source : JSON.stringify(source);

    const result = await this.proxyAiResponseSchemaService.analyzePrompt(
      INSTRUCTIONS.systemInstructionGetProductsDomain,
      promptPayload,
      GeminiSchemaType.DomainNameProductsUsingCompanyResponseSchema,
      0,
      'gemini-flash-latest'
    );

    console.log('gemini', result);
    const company = await this.getCompany(result);
    if (!company) {
      this.selectedCompany = null;
      this.companyNotFound = true;
      this.rows = [];
      this.loading = false;
      this.cdr.detectChanges();
      return;
    }

    this.selectedCompany = company;

    const items = Array.isArray(result?.items)
      ? result.items
      : Array.isArray(result)
      ? result
      : [];

    console.log('productsGm', items);

    const productBC = await this.fetchAllProductsParallel(items);
    this.rows = productBC.map((row) => {
      const product = row.product;
      const hasProduct = !!product?.id;

      if (hasProduct) {
        this.addProductToSelection(product);
      }

      return {
        ...row,
        isSelected: hasProduct,
      };
    });
    console.log('productsBC', productBC);
    this.loading = false;
  }

  async getCompany(result): Promise<CompanyDto | null> {
    console.log(result);
    const domain = result?.CompanyDomainName;

    if (!domain) {
      return null;
    }

    const body: CompanyPageRequestDto = {
      pageNumber: 0,
      pageSize: 1,
      filters: {
        search: '',
        filterType: GlobalSearchResultTypeEnum.COMPANY,
        website: [domain],
      },
      sort: [],
    };

    try {
      const response = await firstValueFrom(
        this.companyService.findCompanies({ body })
      );
      const company = response?.content?.[0] ?? null;

      if (!company) {
        console.warn('Company not found for domain', domain);
      }

      return company;
    } catch (error) {
      console.error('Failed to load company by domain', error);
      return null;
    }
  }

  async fetchAllProductsParallel(dataArray: any[]): Promise<ImportRow[]> {
    const requests = dataArray.map((item) =>
      this.vendorService
        .getProducts({
          vendorDomain: item.ProductVendorDomainName,
          productName: item.ProductName,
          size: 1,
          page: 0,
        })
        .pipe(
          map((res) => ({
            ...item,
            foundCount: res?.totalElements ?? 0,
            product: Array.isArray(res?.content) ? res.content[0] ?? null : null,
          }))
        )
    );

    return await new Promise((resolve, reject) => {
      forkJoin(requests).subscribe({
        next: (results) => resolve(results),
        error: (err) => reject(err),
      });
    });
  }

  toggleRowSelection(row: ImportRow, checked: boolean): void {
    if (!row) {
      return;
    }

    const foundCount = row.foundCount ?? 0;

    if (!checked) {
      row.isSelected = false;
      if (row.product) {
        this.removeProductFromSelection(row.product);
      }
      return;
    }

    if (foundCount === 0) {
      row.isSelected = false;
      return;
    }

    if (foundCount > 1) {
      this.openProductSelectDialog(row);
      return;
    }

    if (row.product) {
      row.isSelected = true;
      this.addProductToSelection(row.product);
    }
  }

  private addProductToSelection(product: ProductDto | null | undefined): void {
    if (!product?.id) {
      return;
    }

    const exists = this.selectedProducts.some((item) => item.id === product.id);

    if (!exists) {
      this.selectedProducts = [...this.selectedProducts, product];
    }
  }

  private removeProductFromSelection(product: ProductDto | null | undefined): void {
    if (!product?.id) {
      return;
    }

    this.selectedProducts = this.selectedProducts.filter(
      (item) => item.id !== product.id
    );
  }

  onClose() {
    this.dialogRef.close();
  }

  openProductSelectDialog(row: ImportRow): void {
    const dialogRef = this.dialog.open(ProductSelectDialogComponent, {
      data: {
        vendorDomain: row.ProductVendorDomainName ?? '',
        productName: row.ProductName ?? '',
      },
    });

    const previousProduct = row.product;
    const wasSelected = !!row.isSelected;

    dialogRef.afterClosed().subscribe((product: ProductDto | null) => {
      const previousSelectedProductId = previousProduct?.id ?? null;

      if (product?.id) {
        if (
          previousSelectedProductId &&
          previousSelectedProductId !== product.id
        ) {
          this.removeProductFromSelection(previousProduct);
        }

        row.product = product;
        row.isSelected = true;
        this.addProductToSelection(product);
      } else {
        row.isSelected = wasSelected;
        row.product = previousProduct ?? null;
      }

      this.cdr.detectChanges();
    });
  }

  async importSelected(): Promise<void> {
    const companyId = this.selectedCompany?.id;
    const products = this.selectedProducts.filter((product) => !!product?.id);

    if (!companyId || !products.length || this.loading || this.importing) {
      return;
    }

    this.importing = true;
    this.cdr.detectChanges();

    const productIds = products.map((product) => product.id!) as string[];

    const existingInstallTasks = productIds.map((productId) =>
      this.fetchInstall(companyId, productId)
    );

    const installProcessing = Promise.all(existingInstallTasks)
      .then((existingInstalls) => {
        const timestamp = new Date().toISOString();

        const updateTasks = productIds.map((productId, index) => {
          const existingInstall = existingInstalls[index];
          const row = this.findRowByProductId(productId);
          const payload = this.buildInstallPayload(
            existingInstall,
            row,
            timestamp
          );

          return this.updateInstall(companyId, productId, payload);
        });

        return Promise.allSettled(updateTasks);
      })
      .then((results) => {
        const rejected = results.filter(
          (result) => result.status === 'rejected'
        );

        if (rejected.length) {
          console.error('Some installs failed to import', rejected);
        }
      })
      .catch((error) => {
        console.error('Failed to import company products', error);
      })
      .finally(() => {
        this.importing = false;
        this.cdr.detectChanges();
      });

    // Close dialog without waiting for all imports to complete.
    this.dialogRef.close({ processing: installProcessing });
  }

  private findRowByProductId(productId: string): ImportRow | undefined {
    return this.rows.find((row) => row.product?.id === productId);
  }

  private buildInstallPayload(
    existing: TechStackInstallDto | null,
    row: ImportRow | undefined,
    timestamp: string
  ): TechStackInstallDto {
    const rowDateFirst = this.normalizeDate(row?.dateFirstVerified);
    const rowDateLast = this.normalizeDate(row?.dateLastVerified);
    const firstVerified =
      existing?.dateFirstVerified ??
      rowDateFirst ??
      rowDateLast ??
      timestamp;

    return {
      dateFirstVerified: firstVerified,
      dateLastVerified: timestamp,
      intensity: (existing?.intensity ?? 0) + 1,
    };
  }

  private async fetchInstall(
    companyId: string,
    productId: string
  ): Promise<TechStackInstallDto | null> {
    try {
      return await firstValueFrom(
        this.installService.getInstall({ companyId, productId })
      );
    } catch (error) {
      if (!this.isIgnorableError(error)) {
        console.warn('Unable to fetch install, defaulting to new payload', {
          companyId,
          productId,
          error,
        });
      }

      return null;
    }
  }

  private async updateInstall(
    companyId: string,
    productId: string,
    payload: TechStackInstallDto
  ): Promise<void> {
    await firstValueFrom(
      this.installService.updateInstall({
        companyId,
        productId,
        body: payload,
      })
    );
  }

  private normalizeDate(value?: string | null): string | null {
    if (!value) {
      return null;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date.toISOString();
  }

  private isIgnorableError(error: unknown): boolean {
    const status = (error as { status?: number })?.status;
    return status === 400 || status === 404;
  }
}
